'use client'

import { useEffect, useState } from 'react'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { getRoles, deleteRole } from '@/lib/api/roles/roles-api'
import { Role } from '@/lib/api/common/interfaces/role.interface'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdClose,
    MdLock,
    MdPeople,
    MdShield,
    MdStar,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'

const USER_TYPE_LABELS: Record<string, string> = {
    PROFESSIONAL: 'Profissional',
    EMPLOYEE: 'Funcionário',
}

export default function PermissionsPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'security',
        'permissions'
    )
    const [roles, setRoles] = useState<Role[]>([])
    const [filteredRoles, setFilteredRoles] = useState<Role[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadRoles()
    }, [])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = roles.filter(
                role =>
                    role.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    role.description
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredRoles(filtered)
        } else {
            setFilteredRoles(roles)
        }
    }, [searchTerm, roles])

    const loadRoles = async () => {
        setLoading(true)
        try {
            const response = await getRoles()
            if (response.success) {
                const result = Array.isArray(response.result)
                    ? response.result
                    : []
                setRoles(result)
                setFilteredRoles(result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar perfis de acesso'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar perfis de acesso')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleDeleteClick = (role: Role) => {
        setRoleToDelete(role)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!roleToDelete) return

        setDeleting(true)
        try {
            const response = await deleteRole(roleToDelete.id)
            if (response.success) {
                toast.success('Perfil de acesso removido com sucesso')
                setRoleToDelete(null)
                setShowDeleteModal(false)
                await loadRoles()
            } else {
                toast.error(
                    response.error || 'Erro ao remover perfil de acesso'
                )
            }
        } catch (error) {
            toast.error('Erro ao remover perfil de acesso')
        } finally {
            setDeleting(false)
        }
    }

    const countModules = (role: Role): number => {
        if (!role.permissions || typeof role.permissions !== 'object') return 0
        const perms = role.permissions as Record<string, any>
        if (perms['*'] === '*') return -1
        return Object.keys(perms).length
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 animate-fade-in">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando perfis de acesso...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdLock className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Perfis de Acesso
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {roles.length}{' '}
                            {roles.length === 1
                                ? 'perfil cadastrado'
                                : 'perfis cadastrados'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/security/permissions/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Novo Perfil</span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome ou descrição..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {filteredRoles.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdShield className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum perfil encontrado'
                                : 'Nenhum perfil de acesso cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Crie perfis de acesso para gerenciar as permissões dos seus colaboradores.'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/security/permissions/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar primeiro perfil
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredRoles.map(role => {
                        const moduleCount = countModules(role)
                        const isFullAccess = moduleCount === -1

                        return (
                            <div
                                key={role.id}
                                className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                                onClick={() =>
                                    navigate(`/security/permissions/${role.id}`)
                                }
                            >
                                <div className="flex items-start gap-4 p-4">
                                    <div className="relative w-14 h-14 flex-shrink-0 bg-gradient-to-br from-neutral/20 to-neutral/10 rounded-xl flex items-center justify-center border border-neutral/20">
                                        <MdShield className="text-2xl text-neutral" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                                    {role.name}
                                                </h3>
                                                {role.isSystem && (
                                                    <span className="badge badge-ghost badge-sm">
                                                        Sistema
                                                    </span>
                                                )}
                                            </div>
                                            {(canUpdate || canDelete) &&
                                                !role.isSystem && (
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    navigate(
                                                                        `/security/permissions/edit/${role.id}`
                                                                    )
                                                                }}
                                                                className="btn btn-circle btn-sm btn-ghost"
                                                                title="Editar perfil"
                                                            >
                                                                <MdEdit className="text-lg" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDeleteClick(
                                                                        role
                                                                    )
                                                                }}
                                                                className="btn btn-circle btn-sm btn-ghost text-error"
                                                                title="Remover perfil"
                                                            >
                                                                <MdDelete className="text-lg" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                        </div>

                                        {role.description && (
                                            <p className="text-sm text-base-content/60 mb-3 line-clamp-2">
                                                {role.description}
                                            </p>
                                        )}

                                        <div className="flex items-center flex-wrap gap-2 pt-3 border-t border-base-300/50">
                                            {role.defaultForType && (
                                                <span className="badge badge-secondary badge-outline gap-1">
                                                    <MdStar className="text-xs" />
                                                    Padrão para{' '}
                                                    {USER_TYPE_LABELS[
                                                        role.defaultForType
                                                    ] || role.defaultForType}
                                                </span>
                                            )}
                                            <span className="badge badge-outline gap-1">
                                                <MdShield className="text-xs" />
                                                {isFullAccess
                                                    ? 'Acesso total'
                                                    : `${moduleCount} ${moduleCount === 1 ? 'módulo' : 'módulos'}`}
                                            </span>
                                            {role.usersCount !== undefined && (
                                                <span className="badge badge-outline gap-1">
                                                    <MdPeople className="text-xs" />
                                                    {role.usersCount}{' '}
                                                    {role.usersCount === 1
                                                        ? 'usuário'
                                                        : 'usuários'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setRoleToDelete(null)
                }}
                onConfirm={handleDeleteConfirm}
                title="Remover perfil de acesso"
                message={`Tem certeza de que deseja remover o perfil "${roleToDelete?.name}"? Os usuários vinculados ficarão sem perfil de acesso definido.`}
                loading={deleting}
                confirmButtonText="Remover"
            />
        </div>
    )
}
